// Closed type sets for core/audit/masters/partners/reporting [03 §3–§5, §17 `e(...)` columns;
// BUILD-PLAN §9 additions; 04 §16; 06 §16]. DB lanes generate named CHECKs from `values`.
import { defineEnum } from './define.ts';
import type { EnumValue } from './define.ts';

// ---- core ----
export const APP_USER_STATUS = defineEnum('core.app_user.status', [
  'invited',
  'active',
  'suspended',
  'reset_pending',
]);
export const LOCALE = defineEnum('core.app_user.locale', ['en', 'fr']);
export type Locale = EnumValue<typeof LOCALE>;
/** Customer-document / translation languages [MB D17]. */
export const DOCUMENT_LANGUAGE = defineEnum('core.data_translation.lang', ['en', 'fr', 'de']);
export type DocumentLanguage = EnumValue<typeof DOCUMENT_LANGUAGE>;
export const PERMISSION_KIND = defineEnum('core.permission.kind', [
  'standard',
  'special',
  'sensitive_view',
]);
export const SEQUENCE_RESET_RULE = defineEnum('core.document_sequence.reset_rule', [
  'never',
  'fiscal_year',
  'calendar_year',
  'monthly',
]);
export type SequenceResetRule = EnumValue<typeof SEQUENCE_RESET_RULE>;
export const SEQUENCE_BUCKET_SCOPE = defineEnum('core.document_sequence.bucket_scope', [
  'none',
  'category_digit',
  'doc_subtype',
]);
/** + `certificate` [04 §16 #9; BUILD-PLAN §9]. */
export const ATTACHMENT_KIND = defineEnum('core.attachment.kind', [
  'original_po',
  'test_report',
  'spec_sketch',
  'style_image',
  'pattern_file',
  'swatch',
  'shipping_doc',
  'label_pdf',
  'invoice',
  'supplier_invoice',
  'bank_statement',
  'export_file',
  'certificate',
  // CR-W2b-12 (CR-W2b-3 R-ATT-1): stored PO documents (were `other`).
  'po_pdf',
  // CR-BG2c: original ETA feed files (W2c.eta-freight R-ATT-1, were `other`); customer remittance advices on receipts
  // (W2c.ar-bank R-ATT-AR-1).
  'eta_import',
  'remittance',
  'other',
]);
export const HELP_FEATURE_STATUS = defineEnum('core.help_feature.status', [
  'draft',
  'published',
  'retired',
]);
export const DOCUMENT_TEMPLATE_KIND = defineEnum('core.document_template.kind', [
  'label_zpl',
  'pdf_html',
  'email',
]);
/** + 04 §16 #11 (tech_pack, cost_sheet, sample_tag) and 06 §16 #6 shipping-document purposes. */
export const DOCUMENT_TEMPLATE_PURPOSE = defineEnum('core.document_template.purpose', [
  'care',
  'bag',
  'hang_tag',
  'barcode',
  'carton',
  'market_sticker',
  'commercial_invoice',
  'packing_list',
  'bill_of_lading',
  'container_list',
  'hs_declaration',
  'ar_invoice',
  'credit_note',
  'po',
  'cutting_slip',
  'ply_sheet',
  'contractor_receipt',
  'report',
  'tech_pack',
  'cost_sheet',
  'sample_tag',
  'delivery_note',
  'broker_document',
  'air_shipment_document',
  'master_shipping_list',
  'shipment_summary',
  'items_to_ship',
  'master_carton_label',
  'skid_label',
  'po_confirmation',
]);
export const PRINTER_KIND = defineEnum('core.printer.kind', ['zpl_label', 'laser', 'tag']);
export const PRINT_PAYLOAD_KIND = defineEnum('core.print_job.payload_kind', ['zpl', 'pdf']);
export const LEGACY_SOURCE_SYSTEM = defineEnum('core.legacy_key_map.source_system', [
  'icon_sql',
  'access',
  'sage_kimdat',
  'shopify',
  'excel',
]);
/** `system_of_record` on every business document [03 §0; BR-DM-014]. */
export const SYSTEM_OF_RECORD = defineEnum('core.document.system_of_record', ['SONNET', 'ICON']);
/** Support ticket priority incl. P1B (data corruption / integrity, 4 h) [02 §20–§22; BUILD-PLAN §9]. */
export const SUPPORT_PRIORITY = defineEnum('core.support_ticket.priority', [
  'P1',
  'P1B',
  'P2',
  'P3',
  'P4',
]);
/** Setting scopes [02 §11 + 04 §16 #14 `style`; 03 `division`; 05/06 contractor, carrier, fabric]. */
export const SETTING_SCOPE = defineEnum('masters.setting_value.scope_type', [
  'global',
  'division',
  'customer',
  'supplier',
  'contractor',
  'carrier',
  'warehouse',
  'fabric',
  'style',
]);
export type SettingScope = EnumValue<typeof SETTING_SCOPE>;

// ---- audit ----
/** 03 audit_log (02 §9 names `api_client`; 03 wins on the stored set: API clients log as `integration`). */
export const ACTOR_KIND = defineEnum('audit.audit_log.actor_kind', [
  'user',
  'device',
  'system',
  'migration',
  'integration',
]);
export type ActorKind = EnumValue<typeof ACTOR_KIND>;
export const AUDIT_ACTION = defineEnum('audit.audit_log.action', [
  'create',
  'update',
  'status_change',
  'deactivate',
  'reactivate',
  'approve',
  'post',
  'reverse',
  'override',
  'delete_draft_line',
  'export',
  'permission_change',
  'sign_in',
]);
export const AUDIT_SOURCE = defineEnum('audit.audit_log.source', [
  'ui',
  'api',
  'job',
  'import',
  'migration',
]);

// ---- masters ----
export const UOM_DIMENSION = defineEnum('masters.uom.dimension', [
  'length',
  'count',
  'mass',
  'area',
]);
export const FX_RATE_SOURCE = defineEnum('masters.fx_rate.source', [
  'manual',
  'boc',
  'sage_extract',
]);
export const PAYMENT_TERM_APPLIES_TO = defineEnum('masters.payment_term.applies_to', [
  'customer',
  'supplier',
  'both',
]);
export const PAYMENT_TERM_DUE_BASIS = defineEnum('masters.payment_term.due_basis', [
  'invoice_date',
  'end_of_month',
  'receipt_date',
  'delivery_date',
]);
export const BUYER_MODULE = defineEnum('masters.buyer.module', ['fabric', 'trim', 'both']);
export const WAREHOUSE_KIND = defineEnum('masters.warehouse.kind', [
  'main',
  'outlet',
  'd2c',
  'contractor',
  'in_transit',
  'samples',
  'quarantine',
  'damages',
]);
export const INVENTORY_CATEGORY_SUBJECT = defineEnum('masters.inventory_category.subject', [
  'fabric',
  'trim',
  'finished',
]);
export const MEASUREMENT_POINT_APPLIES_TO = defineEnum('masters.measurement_point.applies_to', [
  'all',
  'knitted',
  'woven',
]);
export const BOM_PLACEMENT_APPLIES_TO = defineEnum('masters.bom_placement.applies_to', [
  'fabric',
  'trim',
  'both',
]);
export const CARE_INSTRUCTION_KIND = defineEnum('masters.care_instruction.kind', [
  'content',
  'wash',
]);
export const SETTING_VALUE_TYPE = defineEnum('masters.setting_definition.value_type', [
  'int',
  'numeric',
  'pct',
  'money',
  'text',
  'bool',
  'json',
]);
export type SettingValueType = EnumValue<typeof SETTING_VALUE_TYPE>;

// ---- partners ----
export const PARTNER_LANGUAGE = defineEnum('partners.partner.language', ['en', 'fr', 'de']);
export const PARTNER_ADDRESS_KIND = defineEnum('partners.partner_address.kind', [
  'bill_to',
  'ship_to',
  'remit_to',
  'main',
  'mill',
  'warehouse',
]);
export const CUSTOMER_GROUP = defineEnum('partners.customer.customer_group', [
  'tv_retail',
  'retail',
  'wholesale',
  'd2c',
  'staff',
]);
export const CUSTOMER_DISCOUNT_KIND = defineEnum('partners.customer_discount.kind', [
  'discount',
  'volume_rebate',
  'listing_allowance',
  'markdown',
  'co_op',
  'commission',
]);
export const CUSTOMER_DISCOUNT_BASIS = defineEnum('partners.customer_discount.basis', [
  'net',
  'gross',
]);
export const SUPPLIER_KIND = defineEnum('partners.supplier.kind', [
  'fabric',
  'trim',
  'both',
  'service',
  'freight',
  'broker',
]);
export const PAYMENT_METHOD = defineEnum('partners.supplier.payment_method', [
  'wire_tt',
  'cheque',
  'eft',
  'card',
  'lc',
]);
export const CONTRACTOR_KIND = defineEnum('partners.contractor.kind', [
  'cutter',
  'sewer',
  'both',
  'finisher',
]);
export const CONTRACTOR_OPERATION = defineEnum('partners.contractor_rate.operation', [
  'cut',
  'sew',
  'finish',
]);
export const CARRIER_MODE = defineEnum('partners.carrier.mode', [
  'sea',
  'air',
  'road',
  'courier',
  'rail',
]);
export const CARRIER_DIRECTION = defineEnum('partners.carrier.direction', [
  'inbound',
  'outbound',
  'both',
]);
export const FREIGHT_PAYER = defineEnum('partners.carrier.freight_payer', [
  'kimco',
  'customer',
  'supplier',
]);
export const CARRIER_FEED_KIND = defineEnum('partners.carrier.feed_kind', [
  'none',
  'api',
  'file',
  'portal',
]);

// ---- reporting ----
export const REPORT_KIND = defineEnum('reporting.report_definition.kind', [
  'standard',
  'builder',
  'nl_saved',
]);
export const REPORT_FORMAT = defineEnum('reporting.report_run.format', [
  'pdf',
  'xlsx',
  'csv',
  'html',
]);
/** CR-W2b-12 (CR-W2b-5 R-SCH-1): a schedule fires while active; the worker parks it, its owner cancels it. */
export const REPORT_SCHEDULE_STATUS = defineEnum('reporting.report_schedule.status', [
  'active',
  'parked',
  'cancelled',
]);
export const REPORT_RUN_STATUS = defineEnum('reporting.report_run.status', [
  'queued',
  'running',
  'done',
  'failed',
]);
/**
 * CR-BG2e (W2e.nl-assistant R-NLA-8): `pending` = a question that took its quota slot and is still running; only
 * `reporting.nl_query_finish` (the asker's own row) moves it to a final status.
 */
export const NL_QUERY_STATUS = defineEnum('reporting.nl_query_log.status', [
  'pending',
  'ok',
  'rejected',
  'error',
  'timeout',
]);
